/**
 * scroll-animations.js — Premium animation engine for FUEL ENERGY
 * Handles: scroll reveals, cursor, magnetic buttons, word-split,
 *          rating bars, section label lines, scroll progress, parallax,
 *          card tilt, counter animation.
 */
(function () {
  'use strict';

  /* ── PAGE LOADER GATE ──
     While the first-visit loader (snippets/page-loader.liquid) covers the
     page, html carries .is-loading and everything is "in view" behind it, so
     the reveals below would fire unseen. Their observers are queued instead
     and start on `fuel:loader-done`, which fires the moment the panel starts
     lifting. That event always comes: the loader sends it, and the <head>
     script in layout/theme.liquid sends it at 3s if the loader never does. */
  var docEl = document.documentElement;
  var queued = [];
  function watch(observer, el) {
    if (docEl.classList.contains('is-loading')) queued.push([observer, el]);
    else observer.observe(el);
  }
  document.addEventListener('fuel:loader-done', function () {
    queued.forEach(function (q) { q[0].observe(q[1]); });
    queued = [];
  });

  /* ── SCROLL PROGRESS BAR ── */
  var progressBar = document.getElementById('scroll-progress');
  if (progressBar) {
    window.addEventListener('scroll', function () {
      var s = document.documentElement.scrollTop;
      var h = document.documentElement.scrollHeight - window.innerHeight;
      progressBar.style.width = (h > 0 ? (s / h) * 100 : 0) + '%';
    }, { passive: true });
  }

  /* ── HEADER SCROLL TINT ── */
  var header = document.querySelector('.site-header, header');
  if (header) {
    window.addEventListener('scroll', function () {
      header.classList.toggle('scrolled', window.scrollY > 20);
    }, { passive: true });
  }

  /* ── CUSTOM CURSOR (thunderbolt) ── */
  (function () {
    var dot  = document.getElementById('cursor-dot');
    var ring = document.getElementById('cursor-ring');
    if (!dot || !ring) return;
    if (!window.matchMedia('(hover: hover)').matches) return;

    var mx = 0, my = 0, rx = 0, ry = 0;

    document.addEventListener('mousemove', function (e) {
      mx = e.clientX; my = e.clientY;
      dot.style.transform = 'translate(' + mx + 'px,' + my + 'px)';
    }, { passive: true });

    (function lerp() {
      rx += (mx - rx) * 0.1;
      ry += (my - ry) * 0.1;
      ring.style.transform = 'translate(' + rx.toFixed(2) + 'px,' + ry.toFixed(2) + 'px)';
      requestAnimationFrame(lerp);
    })();

    document.addEventListener('mousedown', function () { ring.classList.add('clicking'); });
    document.addEventListener('mouseup',   function () { ring.classList.remove('clicking'); });
    document.addEventListener('mouseleave', function () { dot.style.opacity = '0'; ring.style.opacity = '0'; });
    document.addEventListener('mouseenter', function () { dot.style.opacity = '1'; ring.style.opacity = '1'; });

    document.querySelectorAll('a, button, .flavour-card, .review-card, .bundle-card').forEach(function (el) {
      el.addEventListener('mouseenter', function () { ring.classList.add('hover'); });
      el.addEventListener('mouseleave', function () { ring.classList.remove('hover'); });
    });
  })();

  /* ── MAGNETIC BUTTONS ── */
  document.querySelectorAll('.btn--primary, .btn-primary').forEach(function (btn) {
    btn.addEventListener('mousemove', function (e) {
      var r = btn.getBoundingClientRect();
      var x = (e.clientX - r.left - r.width  / 2) * 0.28;
      var y = (e.clientY - r.top  - r.height / 2) * 0.28;
      btn.style.transform = 'translate(' + x + 'px,' + y + 'px) translateY(-2px)';
      btn.style.boxShadow = '0 ' + (10 + y * .5) + 'px ' + (28 + Math.abs(y)) + 'px rgba(224,36,74,.42)';
    });
    btn.addEventListener('mouseleave', function () {
      btn.style.transform = '';
      btn.style.boxShadow = '';
    });
  });

  /* ── HERO HEADLINE WORD SPLIT ── */
  (function () {
    var h1 = document.querySelector('.hero__headline');
    if (!h1) return;
    var parts = h1.innerHTML.split(/(<br\s*\/?>)/i);
    var out = '';
    var delay = 0.18;
    parts.forEach(function (part) {
      if (/^<br/i.test(part)) { out += '<br>'; return; }
      part.trim().split(/\s+/).forEach(function (w) {
        if (!w) return;
        out += '<span class="hero-word"><span class="w" style="animation-delay:' + delay + 's">' + w + '</span></span> ';
        delay += 0.08;
      });
    });
    h1.innerHTML = out.trim();
  })();

  /* ── SCROLL REVEAL (animate-on-scroll + section-label) ── */
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('.animate-on-scroll, .section-label').forEach(function (el) {
      el.classList.add('is-visible', 'in');
    });
  } else {
    var revealObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible', 'in');
          revealObs.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -48px 0px' });

    document.querySelectorAll('.animate-on-scroll').forEach(function (el) {
      watch(revealObs, el);
    });

    /* Section label animated prefix line */
    document.querySelectorAll('.section-label').forEach(function (el) {
      var line = document.createElement('span');
      line.className = 'label-line';
      el.prepend(line);
      var lo = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) { el.classList.add('in'); lo.disconnect(); }
      }, { threshold: 0.5 });
      watch(lo, el);
    });
  }

  /* ── RATING BARS ── */
  document.querySelectorAll('.rating-bar__fill').forEach(function (bar) {
    var w = bar.style.width || getComputedStyle(bar).width;
    bar.style.setProperty('--target-w', w);
    bar.style.width = '0';
    var bo = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { bar.classList.add('in'); bo.disconnect(); }
    }, { threshold: 0.5 });
    watch(bo, bar);
  });

  /* ── NUMBER COUNTER ── */
  function easeOutQuart(t) { return 1 - Math.pow(1 - t, 4); }
  function animateCounter(el, original, duration) {
    var match = original.match(/([\d,]+)/);
    if (!match) return;
    var end = parseInt(match[0].replace(/,/g, ''), 10);
    var start = null;
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      var val = Math.floor(easeOutQuart(p) * end);
      el.textContent = original.replace(match[0], val.toLocaleString('en-IN'));
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = original;
    }
    requestAnimationFrame(step);
  }

  document.querySelectorAll('.counter-animate, .proof-stat-value, .agg-rating__display-score').forEach(function (el) {
    var raw = el.textContent;
    if (!/\d/.test(raw)) return;
    var co = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting && !el.dataset.counted) {
        el.dataset.counted = '1';
        animateCounter(el, raw, 1600);
        co.disconnect();
      }
    }, { threshold: 0.6 });
    watch(co, el);
  });

  /* ── PARALLAX ON HERO VISUAL ── */
  (function () {
    var vis = document.querySelector('.hero__visual, .hero-visual');
    if (!vis || !window.matchMedia('(hover: hover)').matches) return;
    window.addEventListener('scroll', function () {
      vis.style.transform = 'translateY(' + (window.scrollY * 0.06) + 'px)';
    }, { passive: true });
  })();

  /* ── CARD 3D TILT ── */
  document.querySelectorAll('.flavour-card, .bundle-card--featured').forEach(function (card) {
    card.addEventListener('mousemove', function (e) {
      var r = card.getBoundingClientRect();
      var cx = (e.clientX - r.left) / r.width  - 0.5;
      var cy = (e.clientY - r.top)  / r.height - 0.5;
      card.style.transform = 'translateY(-6px) perspective(800px) rotateX(' + (cy * -5) + 'deg) rotateY(' + (cx * 5) + 'deg)';
    });
    card.addEventListener('mouseleave', function () { card.style.transform = ''; });
  });

  /* ── ANNOUNCEMENT BAR PAUSE ON HOVER ── */
  var ticker = document.querySelector('.ticker-track, .announcement-ticker');
  if (ticker) {
    ticker.addEventListener('mouseenter', function () { ticker.style.animationPlayState = 'paused'; });
    ticker.addEventListener('mouseleave', function () { ticker.style.animationPlayState = 'running'; });
  }

})();
